import { getConfig } from "@/config";
import { emitter } from "@/utils/emitter/mitt";
import {
  SeamlessWebAudioPlayer,
  type WebAudioItem
} from "./seamlessWebAudioCore";

// SeamlessWebAudioPlayer的扩展接口（包含产品切换相关方法）
interface SeamlessWebAudioPlayerExtended extends SeamlessWebAudioPlayer {
  setAllowedProductId?(pid: string | null): void;
  purgeQueuedExceptAllowed?(stopPreScheduled: boolean): void;
  stopCurrentIfDisallowed?(allowedPid: string): void;
  setSuppressAutoNext?(flag: boolean): void;
  addUrgentNext?(item: WebAudioItem): Promise<void>;
}

// ==================== 类型定义 ====================

export interface AudioItem {
  id: string;
  text: string;
  audioUrl: string;
  productId?: string;
  voiceNo?: number;
  duration?: number;
  commentId?: string;
}

export interface PlatformConfig {
  platform: "pc" | "android" | "unknown";
  overlapMs: number;
  fadeMs: number;
  targetGapMs: number;
  ensurePrevComplete: boolean;
  audibleGuardMs: number;
  minStartGain: number;
}

export interface PlayerStatus {
  isPlaying: boolean;
  currentIndex: number;
  totalItems: number;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  platform: string;
  queueLength: number;
}

type ProgressCallback = (index: number, progress: number) => void;
type CompleteCallback = (index: number) => void;
type ErrorCallback = (error: Error, item?: AudioItem) => void;

// ==================== 平台检测 ====================

class PlatformDetector {
  private static _instance: PlatformDetector | null = null;
  private readonly ua: string;
  private readonly platform: "pc" | "android" | "unknown";

  private constructor() {
    this.ua = navigator.userAgent || "";
    if (/Android/i.test(this.ua)) {
      this.platform = "android";
    } else if (/iPhone|iPad|iPod/i.test(this.ua)) {
      this.platform = "pc";
    } else {
      this.platform = "pc";
    }
  }

  static getInstance(): PlatformDetector {
    if (!this._instance) {
      this._instance = new PlatformDetector();
    }
    return this._instance;
  }

  get userAgent() {
    return this.ua;
  }

  get platformName() {
    return this.platform;
  }
}

// ==================== 配置管理 ====================

class ConfigManager {
  private static readonly defaults: Record<string, PlatformConfig> = {
    pc: {
      platform: "pc",
      overlapMs: 200,
      fadeMs: 180,
      targetGapMs: 0,
      ensurePrevComplete: true,
      audibleGuardMs: 20,
      minStartGain: 0.08
    },
    android: {
      platform: "android",
      overlapMs: 150,
      fadeMs: 150,
      targetGapMs: 0,
      ensurePrevComplete: true,
      audibleGuardMs: 20,
      minStartGain: 0.08
    },
    unknown: {
      platform: "unknown",
      overlapMs: 180,
      fadeMs: 180,
      targetGapMs: 0,
      ensurePrevComplete: true,
      audibleGuardMs: 20,
      minStartGain: 0.08
    }
  };

  static getConfig(platform: string): PlatformConfig {
    const detector = PlatformDetector.getInstance();
    const cfg: any = typeof getConfig === "function" ? getConfig() : {};
    const custom = (cfg?.Audio?.UniversalWA ?? {}) as Partial<PlatformConfig>;
    const base = this.defaults[platform] || this.defaults.unknown;
    return {
      ...base,
      ...custom
    };
  }
}

// ==================== 播放器实现 ====================

export class UniversalWebAudioPlayer {
  private readonly roomId: string;
  private readonly platformConfig: PlatformConfig;
  private player: SeamlessWebAudioPlayerExtended | null = null;
  private initialized = false;

  private queue: WebAudioItem[] = [];
  private addedIds: Set<string> = new Set();
  private currentIndex = 0;
  private volume = 1;
  private muted = false;
  private isPlaying = false;

  private onProgress: ProgressCallback | null = null;
  private onComplete: CompleteCallback | null = null;
  private onError: ErrorCallback | null = null;

  // 产品切换相关状态
  private preparingTargetPid: string | null = null;
  private targetPrepared = false;
  private switchTargetReady = false;
  private pendingTargetFeed: WebAudioItem[] = [];
  private switchApplied = false;

  constructor(roomId: string, _config?: Partial<PlatformConfig>) {
    this.roomId = roomId;
    const detector = PlatformDetector.getInstance();
    const platform = detector.platformName;
    this.platformConfig = {
      ...ConfigManager.getConfig(platform),
      ...(_config ?? {})
    };
  }

  async initialize(): Promise<void> {
    if (this.initialized)
      return console.error(
        `[UniversalWA] 已经创建过了播放器 roomId=${this.roomId}`
      );

    console.log(`[UniversalWA] initialize() 开始执行 roomId=${this.roomId}`);

    try {
      this.player = new SeamlessWebAudioPlayer(
        this.roomId
      ) as SeamlessWebAudioPlayerExtended;
      console.log(`[UniversalWA] SeamlessWebAudioPlayer 创建成功`);
    } catch (error) {
      console.error(`[UniversalWA] SeamlessWebAudioPlayer 创建失败:`, error);
      throw error;
    }

    // 参数对齐 iOS 的无缝播控
    try {
      this.player.setOverlap(this.platformConfig.overlapMs / 1000);
      this.player.setFadeMs(this.platformConfig.fadeMs);
      this.player.setTargetGapMs(this.platformConfig.targetGapMs);
      this.player.setMinStartGain(this.platformConfig.minStartGain);
      if (this.platformConfig.ensurePrevComplete) {
        this.player.setEnsurePrevComplete(
          true,
          this.platformConfig.audibleGuardMs
        );
      }
    } catch {}

    this.player.setProgressCallback((index, progress) => {
      this.currentIndex = Math.min(index, Math.max(0, this.queue.length - 1));
      this.isPlaying = true;
      if (this.onProgress) {
        try {
          this.onProgress(this.currentIndex, progress);
        } catch (err) {
          console.warn("[UniversalWA] progress callback error", err);
        }
      }
    });

    this.player.setCompleteCallback(index => {
      const safeIdx = Math.min(index, Math.max(0, this.queue.length - 1));
      const finished = this.queue[safeIdx];
      if (finished) {
        (finished as any).status = "played";
        this.addedIds.delete(finished.id);
      }

      // 边界切换检测：如果目标产品已准备好，在音频自然边界执行切换
      try {
        this.applyTargetSwitch("boundary");
      } catch (error) {
        console.error("[UniversalWA] 边界切换处理错误:", error);
      }

      // 调用外部的完成回调
      if (this.onComplete) {
        try {
          this.onComplete(safeIdx);
        } catch (err) {
          console.warn("[UniversalWA] complete callback error", err);
        }
      }
      this.currentIndex = Math.min(safeIdx + 1, this.queue.length - 1);
      const status = this.player?.getStatus();
      this.isPlaying =
        !!status?.isPlaying && this.currentIndex < this.queue.length;
      if (!this.isPlaying && this.currentIndex >= this.queue.length - 1) {
        emitter.emit("mobileAudioLog", {
          level: "info",
          source: "universal-wa",
          message: "queue drained"
        });
      }
    });

    this.initialized = true;
    console.log(
      `[UniversalWA] initialize() 完成，initialized=${this.initialized}`
    );
  }

  private ensureReady(): asserts this is {
    player: SeamlessWebAudioPlayer;
    initialized: true;
  } {
    console.log(
      `[UniversalWA] ensureReady() 检查: initialized=${this.initialized}, player=${!!this.player}`
    );
    if (!this.initialized || !this.player) {
      this.destroy();
      new Promise(async () => {
        await this.initialize();
      })
        .then(_ => {
          console.info("重新初始化音频");
        })
        .catch(err => {
          console.error("初始化音频播放器失败", err);
        });
      // throw new Error("UniversalWebAudioPlayer not initialized");
    }
  }

  async addToPlaylist(item: AudioItem): Promise<void> {
    this.ensureReady();
    if (!item || !item.id || !item.audioUrl) {
      const err = new Error("Invalid audio item");
      this.onError?.(err, item);
      throw err;
    }

    try {
      console.log("[uwa-debug] addToPlaylist", {
        roomId: this.roomId,
        itemId: item.id,
        voiceNo: item.voiceNo,
        productId: item.productId || null,
        targetPid: this.preparingTargetPid,
        targetPrepared: this.targetPrepared,
        queueLen: this.queue.length
      });
    } catch {}

    // 产品切换检测：如果是目标产品的音频
    if (this.preparingTargetPid && item.productId === this.preparingTargetPid) {
      // 如果是首条目标产品音频且还没开始等待解码
      if (!this.targetPrepared && !this.switchTargetReady) {
        console.log(
          `[UniversalWA] 检测到目标产品首条音频: ${item.id}，等待解码...`
        );
        this.switchTargetReady = true; // 标记正在等待首条解码

        // 继续添加到播放列表（不return）
        // 添加后等待解码完成
        const waitDecoded = async () => {
          const success = await this.waitForAudioDecoded(item.id);
          if (success) {
            this.targetPrepared = true;
            console.log(`[UniversalWA] 目标产品首条音频解码完成: ${item.id}`);

            // 恢复自动播放（目标产品音频已准备好）
            this.setSuppressAutoNext(false);

            try {
              const snapshot = this.queue
                .slice(0, 5)
                .map((q: any, idx: number) => ({
                  idx,
                  id: q?.id,
                  voiceNo: q?.voiceNo,
                  pid: q?.productId || null,
                  status: q?.status
                }));
              emitter.emit("universalWebAudio:targetReady", {
                roomId: this.roomId,
                targetPid: this.preparingTargetPid,
                audioId: item.id,
                queueSnapshot: snapshot
              });
            } catch (err) {
              console.warn("[UniversalWA] emit targetReady failed", err);
            }

            // 如果有缓存的目标产品音频，投喂它们
            if (this.pendingTargetFeed.length > 0) {
              console.log(
                `[UniversalWA] 投喂缓存的目标产品音频: ${this.pendingTargetFeed.length}条`
              );
              const pending = [...this.pendingTargetFeed];
              this.pendingTargetFeed = [];
              for (const pendingItem of pending) {
                try {
                  // 递归调用，但这时targetPrepared已经是true了
                  await this.addToPlaylist(pendingItem);
                } catch {}
              }
            }
          } else {
            console.warn(`[UniversalWA] 目标产品首条音频解码失败: ${item.id}`);
          }
        };

        // 异步等待解码，不阻塞当前添加
        waitDecoded().catch(() => {});
      } else if (!this.targetPrepared && this.switchTargetReady) {
        // 还在等待首条解码，后续的目标产品音频先缓存
        console.log(
          `[UniversalWA] 缓存目标产品音频（等待首条解码）: ${item.id}`
        );
        this.pendingTargetFeed.push(item);
        return; // 暂不投喂
      }
      // 如果targetPrepared已经是true，继续正常添加
    }

    if (this.addedIds.has(item.id)) {
      emitter.emit("mobileAudioLog", {
        level: "warn",
        source: "universal-wa",
        message: `skip duplicate enqueue: ${item.id}`
      });
      return;
    }

    const webItem: WebAudioItem = {
      id: item.id,
      text: item.text,
      audioUrl: item.audioUrl,
      productId: item.productId,
      voiceNo: item.voiceNo,
      storeIndex: this.queue.length,
      commentId: item.commentId || null
    };

    try {
      this.queue.push(webItem);
      webItem.storeIndex = this.queue.length - 1;
      this.addedIds.add(item.id);
      await this.player.addToPlaylist(webItem);
      try {
        const snapshot = this.queue.slice(0, 5).map((q: any, idx: number) => ({
          idx,
          id: q?.id,
          voiceNo: q?.voiceNo,
          pid: q?.productId || null,
          status: q?.status
        }));
        console.log(
          `[uwa-debug] queueAfterAdd roomId=${this.roomId} len=${this.queue.length}`,
          snapshot
        );
      } catch {}
      emitter.emit("mobileAudioLog", {
        level: "info",
        source: "universal-wa",
        message: `added to playlist: ${item.id}`
      });
    } catch (error: any) {
      this.queue.pop();
      this.addedIds.delete(item.id);
      const err = error instanceof Error ? error : new Error(String(error));
      this.onError?.(err, item);
      throw err;
    }
  }

  async addUrgentNext(item: AudioItem): Promise<void> {
    this.ensureReady();
    if (!item || !item.id || !item.audioUrl) return;
    if (this.addedIds.has(item.id)) return;

    const insertPos = Math.min(
      this.queue.length,
      Math.max(0, this.currentIndex + 1)
    );

    const webItem: WebAudioItem = {
      id: item.id,
      text: item.text,
      audioUrl: item.audioUrl,
      productId: item.productId,
      voiceNo: item.voiceNo,
      storeIndex: insertPos,
      commentId: item.commentId || null
    };

    this.queue.splice(insertPos, 0, webItem);
    for (let i = insertPos; i < this.queue.length; i++) {
      this.queue[i].storeIndex = i;
    }

    this.addedIds.add(item.id);

    try {
      if (this.player?.addUrgentNext) {
        await this.player.addUrgentNext(webItem);
      } else {
        throw new Error("addUrgentNext not supported");
      }
      emitter.emit("mobileAudioLog", {
        level: "info",
        source: "universal-wa",
        message: `urgent added: ${item.id}`
      });
    } catch (error: any) {
      this.queue.splice(insertPos, 1);
      this.addedIds.delete(item.id);
      for (let i = insertPos; i < this.queue.length; i++) {
        this.queue[i].storeIndex = i;
      }
      const err = error instanceof Error ? error : new Error(String(error));
      this.onError?.(err, item);
      throw err;
    }
  }

  async play(): Promise<void> {
    this.ensureReady();
    if (this.queue.length === 0) return;
    await this.player.play();
    this.isPlaying = true;
    this.player.setMuted(this.muted);
    this.player.setVolume(this.muted ? 0 : this.volume);
  }

  pause(): void {
    if (!this.player) return;
    this.player.pause();
    this.isPlaying = false;
  }

  async resume(): Promise<void> {
    this.ensureReady();
    this.player.resume();
    this.player.setMuted(this.muted);
    this.player.setVolume(this.muted ? 0 : this.volume);
    this.isPlaying = true;
  }

  stop(): void {
    if (!this.player) return;
    this.player.stop();
    this.queue = [];
    this.addedIds.clear();
    this.currentIndex = 0;
    this.isPlaying = false;
  }

  async next(): Promise<void> {
    this.ensureReady();
    if (this.queue.length === 0) return;
    const nextIdx = Math.min(this.currentIndex + 1, this.queue.length - 1);
    this.player.setCurrentIndex(nextIdx);
    await this.player.play();
  }

  async previous(): Promise<void> {
    this.ensureReady();
    if (this.queue.length === 0) return;
    const prevIdx = Math.max(0, this.currentIndex - 1);
    this.player.setCurrentIndex(prevIdx);
    await this.player.play();
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.player && !this.muted) {
      this.player.setVolume(this.volume);
    }
  }

  getVolume(): number {
    return this.volume;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.player) {
      this.player.setMuted(muted);
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  getStatus(): PlayerStatus {
    const status = this.player?.getStatus();
    return {
      isPlaying: !!status?.isPlaying && this.isPlaying,
      currentIndex: this.currentIndex,
      totalItems: this.queue.length,
      currentTime: status?.currentTime ?? 0,
      duration: status?.duration ?? 0,
      volume: this.volume,
      muted: this.muted,
      platform: this.platformConfig.platform,
      queueLength: this.queue.length
    };
  }

  setProgressCallback(callback: ProgressCallback): void {
    this.onProgress = callback;
  }

  setCompleteCallback(callback: CompleteCallback): void {
    // 只保存外部回调，边界检测逻辑已经在initialize()中设置
    this.onComplete = callback;
  }

  setErrorCallback(callback: ErrorCallback): void {
    this.onError = callback;
  }

  // ==================== 产品切换API ====================

  /**
   * 设置允许的产品ID（用于产品切换过滤）
   */
  setAllowedProductId(pid: string | null): void {
    this.ensureReady();
    if (this.player?.setAllowedProductId) {
      this.player.setAllowedProductId(pid);
    }
  }

  /**
   * 清理队列中非允许产品的待播条目
   */
  purgeQueuedExceptAllowed(stopPreScheduled: boolean = true): void {
    this.ensureReady();
    if (this.player?.purgeQueuedExceptAllowed) {
      this.player.purgeQueuedExceptAllowed(stopPreScheduled);
    }
  }

  /**
   * 停止当前正在播放的非允许产品音频
   */
  stopCurrentIfDisallowed(allowedPid: string): void {
    this.ensureReady();
    if (this.player?.stopCurrentIfDisallowed) {
      this.player.stopCurrentIfDisallowed(allowedPid);
    }
  }

  /**
   * 设置是否抑制自动播放下一条
   */
  setSuppressAutoNext(flag: boolean): void {
    this.ensureReady();
    if (this.player?.setSuppressAutoNext) {
      this.player.setSuppressAutoNext(flag);
    }
  }

  /**
   * 设置准备切换的目标产品ID
   */
  setPreparingTargetPid(pid: string | null): void {
    this.preparingTargetPid = pid;
    this.targetPrepared = false;
    this.switchTargetReady = false;
    this.pendingTargetFeed = [];
    this.switchApplied = false;

    if (pid) {
      console.log(`[UniversalWA] 准备切换到产品: ${pid}`);
    }
  }

  /**
   * 获取准备切换的目标产品ID
   */
  getPreparingTargetPid(): string | null {
    return this.preparingTargetPid;
  }

  /**
   * 设置目标产品准备就绪
   */
  setTargetPrepared(prepared: boolean): void {
    this.targetPrepared = prepared;
    if (prepared) {
      console.log(`[UniversalWA] 目标产品准备就绪: ${this.preparingTargetPid}`);
    }
  }

  /**
   * 获取目标产品是否准备就绪
   */
  isTargetPrepared(): boolean {
    return this.targetPrepared;
  }

  /**
   * 等待音频解码完成
   * @param audioId 音频ID
   * @param timeout 超时时间（毫秒）
   */
  private async waitForAudioDecoded(
    audioId: string,
    timeout: number = 10000
  ): Promise<boolean> {
    if (!this.player) {
      console.warn(`[UniversalWA] waitForAudioDecoded: 播放器未初始化`);
      return false;
    }

    // 调用底层播放器的waitUntilDecoded方法
    const playerAny = this.player as any;
    if (typeof playerAny.waitUntilDecoded === "function") {
      try {
        const success = await playerAny.waitUntilDecoded(audioId, timeout, 60);
        if (success) {
          console.log(`[UniversalWA] 音频解码完成: ${audioId}`);
        } else {
          console.warn(`[UniversalWA] 等待音频解码超时: ${audioId}`);
        }
        return success;
      } catch (error) {
        console.warn(`[UniversalWA] waitUntilDecoded调用失败:`, error);
        return false;
      }
    }

    // 降级方案：简单等待一段时间
    console.warn(`[UniversalWA] waitUntilDecoded不可用，使用降级方案`);
    return new Promise(resolve => {
      setTimeout(() => {
        console.log(`[UniversalWA] 降级等待完成: ${audioId}`);
        resolve(true);
      }, 1000); // 等待1秒
    });
  }

  /**
   * 清理产品切换状态
   */
  clearSwitchState(): void {
    this.preparingTargetPid = null;
    this.targetPrepared = false;
    this.switchTargetReady = false;
    this.pendingTargetFeed = [];
    this.switchApplied = false;
  }

  forceSwitchToTarget(): void {
    this.applyTargetSwitch("force");
  }

  private applyTargetSwitch(reason: "boundary" | "force"): void {
    if (!this.preparingTargetPid || !this.targetPrepared) {
      return;
    }
    if (this.switchApplied) {
      return;
    }

    const targetPid = this.preparingTargetPid;
    this.switchApplied = true;

    console.log(
      `[UniversalWA] ${reason === "force" ? "强制" : "边界"}切换到产品: ${targetPid}`
    );

    try {
      this.setSuppressAutoNext(true);
    } catch {}

    const beforeStopIndex = this.currentIndex;
    const beforeStopItem: WebAudioItem | undefined =
      this.queue[beforeStopIndex];
    try {
      this.stopCurrentIfDisallowed(targetPid);
    } catch (error) {
      console.warn("[UniversalWA] stopCurrentIfDisallowed 失败", error);
    }

    if (beforeStopItem && beforeStopItem.productId !== targetPid) {
      console.log(
        `[UniversalWA] 同步删除外部queue: index=${beforeStopIndex}, id=${beforeStopItem.id}`
      );
      this.queue.splice(beforeStopIndex, 1);
      if (beforeStopItem.id) {
        this.addedIds.delete(beforeStopItem.id);
      }
      for (let i = beforeStopIndex; i < this.queue.length; i++) {
        this.queue[i].storeIndex = i;
      }
    }

    try {
      this.setAllowedProductId(targetPid);
    } catch {}

    const beforePurgeLength = this.queue.length;
    try {
      this.purgeQueuedExceptAllowed(true);
    } catch (error) {
      console.warn("[UniversalWA] purgeQueuedExceptAllowed 失败", error);
    }

    for (let i = this.queue.length - 1; i >= 0; i--) {
      const item = this.queue[i];
      if (item && item.productId && item.productId !== targetPid) {
        console.log(
          `[UniversalWA] 同步清理外部queue: index=${i}, id=${item.id}, pid=${item.productId}`
        );
        this.queue.splice(i, 1);
        if (item.id) {
          this.addedIds.delete(item.id);
        }
      }
    }

    for (let i = 0; i < this.queue.length; i++) {
      this.queue[i].storeIndex = i;
    }
    console.log(
      `[UniversalWA] 队列同步完成: 清理前=${beforePurgeLength}, 清理后=${this.queue.length}`
    );

    const snapshot = this.queue.map(item => ({
      id: item.id,
      productId: item.productId ?? null,
      voiceNo: item.voiceNo ?? null,
      status: (item as any).status ?? "pending",
      text: item.text ?? ""
    }));

    emitter.emit("universalWebAudio:syncQueue", {
      roomId: this.roomId,
      targetPid,
      queueSnapshot: snapshot,
      currentIndex: this.currentIndex,
      isPlaying: this.isPlaying
    });

    emitter.emit("universalWebAudio:boundarySwitch", {
      roomId: this.roomId,
      targetPid
    });

    this.currentIndex = Math.max(
      0,
      Math.min(this.currentIndex, this.queue.length - 1)
    );

    const resumeAutoNext = () => {
      try {
        this.setSuppressAutoNext(false);
      } catch {}
    };

    if (reason === "force") {
      // 强制切换时，立即恢复播放目标产品队列
      try {
        if (this.queue.length > 0) {
          const targetIndex = Math.max(
            0,
            this.queue.findIndex(item => item.productId === targetPid)
          );
          if (targetIndex >= 0) {
            this.currentIndex = targetIndex;
            this.player?.setCurrentIndex?.(targetIndex);
          }
        }
      } catch (error) {
        console.warn("[UniversalWA] setCurrentIndex 失败", error);
      }

      try {
        this.player?.play();
        this.isPlaying = true;
      } catch (error) {
        console.warn("[UniversalWA] 强制切换后播放失败", error);
      }

      setTimeout(resumeAutoNext, 0);
    } else {
      setTimeout(resumeAutoNext, 100);
    }
  }

  destroy(): void {
    try {
      this.player?.destroy();
    } catch {}
    this.player = null;
    this.queue = [];
    this.addedIds.clear();
    this.isPlaying = false;
    this.initialized = false;
  }
}

// ==================== 工厂方法 ====================

export function createUniversalWebAudioPlayer(
  roomId: string,
  customConfig?: Partial<PlatformConfig>
): UniversalWebAudioPlayer {
  return new UniversalWebAudioPlayer(roomId, customConfig);
}

export { PlatformDetector, ConfigManager };
export type { AudioItem, PlatformConfig };
